// ===== Packages =====

import styled                       from 'styled-components';

// ===== Constants =====

import FONT_TYPE                    from '../../constants/fontType';
import {
    CART_AND_CHECKOUT_FONT_SIZE_LARGE,
    CART_AND_CHECKOUT_FONT_SIZE_SMALL,
    BODY_FONT_SIZE,
}                                   from '../../constants/generalConstants';

const ORDER_SUMMARY_CONTAINER_WIDTH = 21.875 * BODY_FONT_SIZE;
const PROMOTION_CODE_ADD_BUTTON_WIDTH = 4.0625 * BODY_FONT_SIZE;
const PROMOTION_CODE_ADD_BUTTON_MARGIN_LEFT = 0.625 * BODY_FONT_SIZE;
const EXTRA_SMALL_CONTENT_TOP_OFFSET = 3.5 * BODY_FONT_SIZE;

export const Container = styled.div`
    width: 100%;
    height: 100vh;
    height: 100svh;
    height: 100dvh;
    background: ${({ theme }) => theme.color.white};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        height: 100vh;
        height: 100svh;
        height: 100dvh;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        touch-action: pan-y;
        padding-bottom: ${`${EXTRA_SMALL_CONTENT_TOP_OFFSET}px`};
    }
`;

export const ContentContainer = styled.div`
    position: absolute;
    top: ${`${5.625 * BODY_FONT_SIZE}px`};
    left: 0;
    display: flex;
    flex-direction: row;
    width: 100%;
    height: ${`calc(100vh - ${5.625 * BODY_FONT_SIZE}px)`};
    height: ${`calc(100svh - ${5.625 * BODY_FONT_SIZE}px)`};
    height: ${`calc(100dvh - ${5.625 * BODY_FONT_SIZE}px)`};
    min-height: 0;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        position: relative;
        top: ${`${EXTRA_SMALL_CONTENT_TOP_OFFSET}px`};
        height: auto;
        min-height: ${`calc(100vh - ${EXTRA_SMALL_CONTENT_TOP_OFFSET}px)`};
        min-height: ${`calc(100svh - ${EXTRA_SMALL_CONTENT_TOP_OFFSET}px)`};
        min-height: ${`calc(100dvh - ${EXTRA_SMALL_CONTENT_TOP_OFFSET}px)`};
        flex-direction: column;
    }
`;

export const CheckpointContainer = styled.div`
    position: relative;
    flex: 1 1 auto;
    padding: ${`${1.25 * BODY_FONT_SIZE}px`};
    padding-bottom: 0px;
    height: 100%;
    min-height: 0;
    max-width: ${`calc(100% - ${ORDER_SUMMARY_CONTAINER_WIDTH}px)`};

    ${({ theme }) => theme.mediaQuery.small} {
        padding: ${`${0.625 * BODY_FONT_SIZE}px`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding: ${`${0.625 * BODY_FONT_SIZE}px`};
        flex: 0 0 auto;
        max-width: none;
    }
`;

export const OrderSummaryContainer = styled.div`
    position: relative;
    flex: ${`0 0 ${ORDER_SUMMARY_CONTAINER_WIDTH}px`};
    padding: ${`${1.25 * BODY_FONT_SIZE}px`};
    height: 100%;
    min-height: 0;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch; /* smooth scrolling for iOS */
    touch-action: pan-y;

    ${({ theme }) => theme.mediaQuery.small} {
        padding: ${`${0.625 * BODY_FONT_SIZE}px`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding: ${`${0.625 * BODY_FONT_SIZE}px`};
        overflow-y: hidden;
        flex: 0 0 auto;
        height: auto;
        padding-bottom: ${`${0.625 * BODY_FONT_SIZE}px`};
    }
`;

export const Header = styled.div`
    position: relative;
    justify-content: space-between;
    width: 100%;
    padding-bottom: ${`${0.625 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    border-bottom: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding: ${`${0.625 * BODY_FONT_SIZE}px`};
        border: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
        background: ${({ theme }) => theme.color.neutral300};
    }
`;

export const OrderSummaryToggleButton = styled.div`
    width: 0; 
    height: 0; 
    border-left: ${`${0.3125 * BODY_FONT_SIZE}px solid transparent`};
    border-right: ${`${0.3125 * BODY_FONT_SIZE}px solid transparent`};
    border-top: ${({ theme }) => `${0.3125 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
    text-transform: uppercase;
    background: ${({ theme }) => theme.color.white};
`;

interface OrderSummaryToggleButtonContainerProps {
    orderSummaryIsVisible: boolean,
    detectTouchDevice: boolean,
}
export const OrderSummaryToggleButtonContainer = styled.div<OrderSummaryToggleButtonContainerProps>`
    position: absolute;
    top: 50%;
    right: ${`${0.3125 * BODY_FONT_SIZE}px`};
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${`${1.25 * BODY_FONT_SIZE}px`};
    width: ${`${1.25 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.625 * BODY_FONT_SIZE}px`};
    padding: ${`${0.625 * BODY_FONT_SIZE}px`};
    margin-left: ${`${0.625 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    transform: ${({ orderSummaryIsVisible }) => `translateY(-50%) rotate(${orderSummaryIsVisible ? 180 : 0}deg)`};

    &:hover {
        ${({ detectTouchDevice, theme }) => (!detectTouchDevice && `
            background: ${theme.verascopeColor.yellow200};
        `)}
    }

    &:hover ${OrderSummaryToggleButton} {
        ${({ detectTouchDevice, theme }) => (!detectTouchDevice && `
            border-top: ${0.3125 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100};
            background: ${theme.verascopeColor.yellow200};
        `)}
    }
`;

interface OrderSummaryProps {
    isVisible: boolean,
}
export const OrderSummary = styled.div<OrderSummaryProps>`
    height: ${({ isVisible }) => (isVisible
        ? 'auto'
        : '0px'
    )};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding: ${`0px ${0.625 * BODY_FONT_SIZE}px`};
    }
    
`;

export const BreadcrumbContainer = styled.div`
    padding-bottom: ${`calc(${0.625 * BODY_FONT_SIZE}px + 0.1em)`}; // Add 0.1 em so that breadcrumb is flush with order summary title
    border-bottom: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
`;

export const CartItemsContainer = styled.div`
    margin-bottom: ${`${1.25 * BODY_FONT_SIZE}px`};
`;

export const CartItemFinalQuantityContainer = styled.span`
`;

export const CartItemFinalQuantityValue = styled.span`
    color: ${({ theme }) => theme.verascopeColor.purple400};
    margin-left: ${`${0.9375 * BODY_FONT_SIZE}px`};
`;

interface PromotionCodeContainerProps {
    noBorder?: boolean,
}
export const PromotionCodeContainer = styled.div<PromotionCodeContainerProps>`
    width: 100%;
    margin-bottom: ${`${1.25 * BODY_FONT_SIZE}px`};
    border-bottom: ${({ noBorder, theme }) => (noBorder
        ? 'none'
        : `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`
    )};
`;

interface PromotionCodeHeaderProps {
    customCursorActive: boolean,
}
export const PromotionCodeHeader = styled.div<PromotionCodeHeaderProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    background: ${({ theme }) => theme.color.white};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
`;

export const PromotionCodeHeaderText = styled.h3`
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE}em`};
    color: ${({ theme }) => theme.verascopeColor.purple100};
    text-transform: uppercase;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }
`;

export const PromotionCodeToggleButton = styled.div`
    width: 0; 
    height: 0; 
    border-left: ${`${0.3125 * BODY_FONT_SIZE}px solid transparent`};
    border-right: ${`${0.3125 * BODY_FONT_SIZE}px solid transparent`};
    border-top: ${({ theme }) => `${0.3125 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
    text-transform: uppercase;
    background: ${({ theme }) => theme.color.white};
`;

interface PromotionCodeToggleButtonContainerProps {
    detectTouchDevice: boolean,
    customCursorActive: boolean,
    promotionCodeInputIsVisible: boolean,
}
export const PromotionCodeToggleButtonContainer = styled.div<PromotionCodeToggleButtonContainerProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${`${1.875 * BODY_FONT_SIZE}px`};
    width: ${`${1.875 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.9375 * BODY_FONT_SIZE}px`};
    padding: ${`${0.625 * BODY_FONT_SIZE}px`};
    margin-left: ${`${0.625 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    transform: ${({ promotionCodeInputIsVisible }) => `rotate(${promotionCodeInputIsVisible ? 180 : 0}deg)`};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};

    &:hover {
        ${({ detectTouchDevice, theme }) => (!detectTouchDevice && `
            background: ${theme.verascopeColor.yellow200};
        `)}
    }

    &:hover ${PromotionCodeToggleButton} {
        ${({ detectTouchDevice, theme }) => (!detectTouchDevice && `
            border-top: ${0.3125 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100};
            background: ${theme.verascopeColor.yellow200};
        `)}
    }
`;

interface PromotionCodeBodyProps {
    isVisible: boolean,
}
export const PromotionCodeBody = styled.div<PromotionCodeBodyProps>`
    display: ${({ isVisible }) => (isVisible
        ? 'block'
        : 'none'
    )};
    padding-bottom: ${`${0.625 * BODY_FONT_SIZE}px`};
`;

interface PromotionCodeInputProps {
    customCursorActive: boolean,
    isInvalid: boolean,
    addButtonVisible: boolean,
}
export const PromotionCodeInput = styled.input<PromotionCodeInputProps>`
    display: inline-block;
    vertical-align: middle;
    border: ${({ isInvalid, theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${isInvalid
        ? theme.verascopeColor.red200
        : theme.verascopeColor.purple100
    }`};
    height: ${`${2.5 * BODY_FONT_SIZE}px`};
    width: ${({ addButtonVisible }) => (addButtonVisible
        ? `calc(100% - ${PROMOTION_CODE_ADD_BUTTON_WIDTH + PROMOTION_CODE_ADD_BUTTON_MARGIN_LEFT}px)`
        : '100%')};
    outline: none;
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.1}em`};
    color: ${({ isInvalid, theme }) => (isInvalid
        ? theme.verascopeColor.red200
        : theme.color.neutral900
    )};
    padding: ${`0px ${0.625 * BODY_FONT_SIZE}px`};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'text')};
    background-color: transparent;

    &:-ms-input-placeholder {
        color: ${({ theme }) => theme.color.neutral500};
    }

    &::-webkit-input-placeholder {
        color: ${({ theme }) => theme.color.neutral500};
    }

    &::placeholder {
        color: ${({ theme }) => theme.color.neutral500};
    }

    &::-ms-input-placeholder {
        color: ${({ theme }) => theme.color.neutral500};
    }

    &:focus {
        color: ${({ isInvalid, theme }) => (isInvalid
        ? theme.verascopeColor.red200
        : theme.verascopeColor.orange200
    )};
        border: ${({ theme, isInvalid }) => `${0.1875 * BODY_FONT_SIZE}px solid ${isInvalid
        ? theme.verascopeColor.red200
        : theme.verascopeColor.orange200
    }`};

        &:-ms-input-placeholder {
                color: ${({ theme }) => `${theme.verascopeColor.orange200}`};
        }

        &::-webkit-input-placeholder {
                color: ${({ theme }) => `${theme.verascopeColor.orange200}`};
        }

        &::placeholder {
                color: ${({ theme }) => `${theme.verascopeColor.orange200}`};
        }

        &::-ms-input-placeholder {
                color: ${({ theme }) => `${theme.verascopeColor.orange200}`};
        }
    }

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }
`;

interface PromotionCodeAddButtonProps {
    customCursorActive: boolean,
    detectTouchDevice: boolean,
    isInvalid: boolean,
    isValidatingCode: boolean,
}
export const PromotionCodeAddButton = styled.button<PromotionCodeAddButtonProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    outline: none;
    border: none;
    background: none;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    padding: 0px;
    flex: ${`0 0 ${4.0625 * BODY_FONT_SIZE}px`};
    width: ${`${PROMOTION_CODE_ADD_BUTTON_WIDTH}px`};
    height: ${`${2.5 * BODY_FONT_SIZE}px`};
    padding: ${`${0.9375 * BODY_FONT_SIZE}px`};
    margin-left: ${`${PROMOTION_CODE_ADD_BUTTON_MARGIN_LEFT}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.1}em`};
    color: ${({ isInvalid, theme }) => (isInvalid
        ? theme.color.neutral500
        : theme.color.white
    )};
    text-transform: uppercase;
    background: ${({ isValidatingCode, isInvalid, theme }) => {
        if (isInvalid) {
            return 'transparent';
        }

        if (isValidatingCode) {
            return theme.color.neutral300;
        }

        return theme.verascopeColor.purple100;
    }};

    ${({ isInvalid, isValidatingCode, theme }) => !isInvalid && !isValidatingCode && `
        &:hover {
        background: ${theme.verascopeColor.orange200};
    }`}

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }
    
    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }
`;

export const PromotionCodeBadgeContainer = styled.div``;

export const PromotionCodeBadge = styled.div`
    position: relative;
    display: inline-block;
    width: fit-content;
    padding: ${`${0.46875 * BODY_FONT_SIZE}px`};
    padding-right: ${`${0.3125 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.1}em`};
    color: ${({ theme }) => theme.verascopeColor.orange100};
    text-transform: uppercase;
    border: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.orange200}`};
    background: ${({ theme }) => theme.color.white};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }
`;

interface PromotionCodeBadgeRemoveButtonProps {
    customCursorActive: boolean,
    detectTouchDevice: boolean,
}
export const PromotionCodeBadgeRemoveButton = styled.button<PromotionCodeBadgeRemoveButtonProps>`
    outline: none;
    border: none;
    background: none;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    padding: 0px;
    width: ${`${1.25 * BODY_FONT_SIZE}px`};
    height: ${`${1.25 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.9375 * BODY_FONT_SIZE}px`};
    padding: ${`${0.3125 * BODY_FONT_SIZE}px`};
    margin-left: ${`${0.46875 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
        overflow: visible;
    }

    & path, & rect {
        fill: ${({ theme }) => theme.verascopeColor.orange100};
        transform: scale(1);
        transform-origin: 50% 50% 0;
    }

    &:hover {
        background: ${({ theme }) => theme.verascopeColor.orange200};
        & path, & rect {
        fill: ${({ theme }) => theme.color.white};
    }
    }
`;

export const PlaceholderCheckpointContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: absolute;
    top: ${`${12.5 * BODY_FONT_SIZE}px`};
    left: 50%;
    transform: translateX(-50%);

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        height: ${`${3.125 * BODY_FONT_SIZE}px`};
        width: ${`${3.125 * BODY_FONT_SIZE}px`};
        animation: rotate 2s linear infinite;
    }

    & svg {
        width: 100%;
        height: 100%;
    }

    & path, & rect {
        fill: ${({ theme }) => theme.verascopeColor.purple100};
    }
`;

export const PlaceholderCheckpointText = styled.h3`
    width: 20ch;
    margin: 0;
    padding: 0;
    margin-top: ${`${1.25 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE}em`};
    line-height: 2em;
    color: ${({ theme }) => theme.verascopeColor.purple300};
    text-transform: uppercase;
    text-align: center;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }
`;

export const OrderSummaryDisclaimer = styled.p`
    margin: 0;
    margin-top: ${`${0.9375 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.1}em`};
    line-height: 1.6em;
    color: ${({ theme }) => theme.verascopeColor.purple300};
    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
        text-align: center;
        margin-bottom: ${`${0.625 * BODY_FONT_SIZE}px`};
    }
`;
